import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useForm } from "./FormContext";

const translations = {
  en: {
    name: 'Full Name',
    phone: 'Phone',
    fieldRequired: 'This field is required.',
    invalidEmail: 'Please enter a valid email address.',
    invalidPhone: 'Phone number can only contain digits.',
  },
  pl: {
    name: 'Imię Nazwisko',
    phone: 'Telefon',
    fieldRequired: 'To pole jest wymagane.',
    invalidEmail: 'Proszę wprowadzić poprawny adres email.',
    invalidPhone: 'Numer telefonu może zawierać tylko cyfry.',
  },
};

// Funkcje walidacyjne
const validateEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  if (!phone) return false;
  const phoneRegex = /^\d+$/;
  return phoneRegex.test(phone);
};

export const Pg2 = forwardRef(({ locale }, ref) => {
  const { state, handleChange } = useForm();
  const { username, phone, email } = state;
  const [errors, setErrors] = useState({
    username: '',
    phone: '',
    email: ''
  });
  const [touched, setTouched] = useState({
    username: false,
    phone: false,
    email: false
  });

  function getTranslation(key) {
    return translations[locale] && translations[locale][key] ? translations[locale][key] : translations['pl'][key];
  };

  // Udostępnij funkcję walidacji komponentowi nadrzędnemu
  useImperativeHandle(ref, () => ({
    validateForm: () => {
      const newErrors = validateAllFields();
      setErrors(newErrors);
      setTouched({
        username: true,
        phone: true,
        email: true
      });
      return Object.values(newErrors).every(error => error === '');
    }
  }));

  const validateAllFields = () => {
    const newErrors = {
      username: '',
      phone: '',
      email: ''
    };

    // Walidacja username
    if (!username?.trim()) {
      newErrors.username = getTranslation('fieldRequired');
    }

    // Walidacja phone
    if (!phone?.trim()) {
      newErrors.phone = getTranslation('fieldRequired');
    } else if (!validatePhone(phone)) {
      newErrors.phone = getTranslation('invalidPhone');
    }

    // Walidacja email
    if (!email?.trim()) {
      newErrors.email = getTranslation('fieldRequired');
    } else if (!validateEmail(email)) {
      newErrors.email = getTranslation('invalidEmail');
    }

    return newErrors;
  };

  const handleFieldChange = (field, value) => {
    handleChange(field, value);

    // Czyść błąd gdy użytkownik edytuje pole
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Walidacja po opuszczeniu pola
    const newErrors = validateAllFields();
    setErrors(newErrors);
  };

  // Funkcja do sprawdzenia czy pole ma błąd i powinno pokazywać komunikat
  const shouldShowError = (field) => {
    return touched[field] && errors[field];
  };

  return (
    <form>
      <div>
        <label id="requiredLabel1" className="form-label">
          {getTranslation('name')}*
        </label>
        <input
          id="requiredInput1"
          type="text"
          value={username || ''}
          onChange={(e) => handleFieldChange("username", e.currentTarget.value)}
          onBlur={() => handleBlur("username")}
          className={`input1 ${shouldShowError('username') ? 'error' : ''}`}
          aria-describedby="requiredWarning1"
        />
        <span
          id="requiredWarning1"
          style={{
            color: 'red',
            display: shouldShowError('username') ? 'block' : 'none'
          }}
        >
          {errors.username}
        </span>
      </div>
      <div className="flexContainer">
        <div className="box">
          <label id="requiredLabel2" className="form-label">
            {getTranslation('phone')}*
          </label>
          <br />
          <input
            id="requiredInput2"
            type="text"
            value={phone || ''}
            onChange={(e) => handleFieldChange("phone", e.currentTarget.value)}
            onBlur={() => handleBlur("phone")}
            className={`input2 ${shouldShowError('phone') ? 'error' : ''}`}
            aria-describedby="requiredWarning2"
          />
          <span
            id="requiredWarning2"
            style={{
              color: 'red',
              display: shouldShowError('phone') ? 'block' : 'none'
            }}
          >
            {errors.phone}
          </span>
        </div>
        <div className="box">
          <label id="requiredLabel3" className="form-label">
            Email*
          </label>
          <br />
          <input
            id="requiredInput3"
            type="email"
            value={email || ''}
            onChange={(e) => handleFieldChange("email", e.currentTarget.value)}
            onBlur={() => handleBlur("email")}
            className={`input2 ${shouldShowError('email') ? 'error' : ''}`}
            aria-describedby="requiredWarning3"
          />
          <span
            id="requiredWarning3"
            style={{
              color: 'red',
              display: shouldShowError('email') ? 'block' : 'none'
            }}
          >
            {errors.email}
          </span>
        </div>
      </div>
    </form>
  );
});